import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { EmergencyServicesConnect } from './EmergencyServicesConnect';
import { TeleTriageConnect } from './TeleTriageConnect';
import { TriageOutcome } from '@/types/triage';

interface UrgentCareConnectProps {
  triageOutcome?: TriageOutcome;
  symptoms?: string[];
}

export function UrgentCareConnect({ triageOutcome, symptoms = [] }: UrgentCareConnectProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>(
    triageOutcome?.urgency === 'immediate' ? 'emergency' : 'tele'
  );

  // If we don't have outcome data, use mock data
  const mockOutcome: TriageOutcome = {
    urgency: 'urgent',
    manchesterCategory: 'Orange - Very Urgent (10 minutes)',
    recommendation: 'Seek medical attention within 2-4 hours.',
    redFlags: ['Moderate fever with headache'],
    clinicalNotes: 'Patient presents with headache, fever, and fatigue. Symptoms moderate severity with gradual onset, lasting 2 days.',
    nextSteps: [
      'Connect with a tele-doctor for immediate guidance',
      'Prepare to visit an urgent care facility if recommended',
      'Monitor symptoms closely'
    ],
    estimatedWaitTime: '10-30 minutes',
    presentingComplaint: 'Headache with fever',
    discriminator: 'moderate severity',
    aiConfidence: 0.85
  };

  const displayOutcome = triageOutcome || mockOutcome;
  const displaySymptoms = symptoms.length > 0 ? symptoms : ['headache', 'fever', 'fatigue'];

  const handleBackToResults = () => {
    navigate('/patient/audio-triage/results');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={handleBackToResults}
          className="mb-4 flex items-center text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Assessment Results
        </Button>

        <h1 className="text-2xl font-bold mb-6 text-center">Urgent Care Connection</h1>

        {/* Tabs for different connection types */}
        <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="emergency">Emergency Services</TabsTrigger>
            <TabsTrigger value="tele">Tele-Triage</TabsTrigger>
          </TabsList>
          
          <TabsContent value="emergency" className="mt-0">
            <EmergencyServicesConnect 
              urgencyLevel={displayOutcome.urgency === 'immediate' ? 'immediate' : 'urgent'}
              symptoms={displaySymptoms}
              patientLocation={{
                address: "123 Patient Street, Cityville, ST 12345"
              }}
            />
          </TabsContent>
          
          <TabsContent value="tele" className="mt-0">
            <TeleTriageConnect 
              urgencyLevel={displayOutcome.urgency === 'urgent' ? 'urgent' : 'standard'}
              symptoms={displaySymptoms}
            />
          </TabsContent>
        </Tabs>

        {/* Disclaimer */}
        <p className="text-xs text-slate-600 text-center max-w-md mx-auto mt-8">
          This service is intended to help connect you with appropriate care based on your symptoms. 
          Always use your best judgment regarding the urgency of your medical situation.
        </p>
      </div>
    </div>
  );
}
